import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { config } from '../config';
import { AUTH_CONSTANTS } from '../shared/constants';

export interface Message {
  type: string;
  email?: string;
  token?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private subject: Subject<any> | null = null;
  // Use a persistent Subject so subscribers don't lose the connection when it's re-established
  private messagesSubject = new Subject<Message>();
  public messages$: Observable<Message> = this.messagesSubject.asObservable();
  private ws: WebSocket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectInterval: number = 3000; // 3 seconds
  private intentionalClosure: boolean = false;
  private statusCheckInterval: any = null;
  // Track the subscription to properly clean up on reconnection
  private wsSubscription: Subscription | null = null;

  constructor() {
    console.log('%c [WebSocketService] Constructor called', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;');

    // Initialize the connection immediately
    setTimeout(() => {
      console.log('%c [WebSocketService] Auto-initializing connection', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;');
      this.initializeConnection();
    }, 1000);
  }

  public initializeConnection(isReconnect: boolean = false): void {
    if (!this.isConnected) {
      console.log('%c [WebSocketService] Initializing WebSocket connection', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;', { isReconnect });
      // Reset intentional closure flag when we're explicitly connecting
      this.intentionalClosure = false;
      // Only reset reconnect attempts on fresh connections, not reconnects
      if (!isReconnect) {
        this.reconnectAttempts = 0;
      }

      // Generate a unique client ID to track this connection
      const clientId = 'client_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
      console.log('%c [WebSocketService] Client ID:', 'background: #9b59b6; color: white; padding: 2px 5px; border-radius: 2px;', clientId);

      // Store client ID in localStorage to identify this browser window
      localStorage.setItem('ws_client_id', clientId);

      // Clean up any existing subscription before creating a new one
      if (this.wsSubscription) {
        console.log('%c [WebSocketService] Cleaning up previous subscription before reconnect', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;');
        this.wsSubscription.unsubscribe();
        this.wsSubscription = null;
      }

      // Subscribe to the raw WebSocket and push parsed messages to the persistent Subject
      this.wsSubscription = this.connect().pipe(
        map((response: MessageEvent): Message => {
          console.log('%c [WebSocketService] Received message for client:', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;', {
            clientId: localStorage.getItem('ws_client_id'),
            data: response.data
          });
          try {
            // Check if response.data exists and is not undefined
            if (typeof response.data === 'undefined' || response.data === null) {
              console.warn('%c [WebSocketService] Received message with undefined/null data:', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;', response);
              return { type: 'error', error: 'No data in message' };
            }

            return JSON.parse(response.data);
          } catch (e) {
            console.error('%c [WebSocketService] Error parsing message:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', {
              error: e,
              messageData: response.data,
              messageType: typeof response.data
            });
            return { type: 'error', error: 'Failed to parse JSON' };
          }
        })
      ).subscribe({
        next: (message) => {
          // Push to the persistent Subject so all subscribers receive the message
          this.messagesSubject.next(message);
        },
        error: (error) => {
          console.error('%c [WebSocketService] WebSocket stream error:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', error);
        }
      });

      // Clear any existing status check interval
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval);
      }

      // Emit a diagnostic message every 60 seconds to verify the connection is active
      this.statusCheckInterval = setInterval(() => {
        if (this.isConnected) {
          console.log('%c [WebSocketService] Connection status check:', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;', {
            clientId: localStorage.getItem('ws_client_id'),
            connected: this.isConnected,
            readyState: this.ws ? this.getReadyStateString(this.ws.readyState) : 'No WebSocket'
          });
        }
      }, 60000); // Reduced frequency to 60 seconds
    }
  }

  public connect(): Observable<MessageEvent> {
    if (null === this.subject || !this.isConnected) {
      this.subject = this.create();
      console.log('%c [WebSocketService] WebSocket connection created', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;');
    } else {
      console.log('%c [WebSocketService] Using existing WebSocket connection', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;');
    }
    return this.subject.asObservable() as Observable<MessageEvent>;
  }

  private create(): Subject<any> {
    // Get JWT token for authentication using the correct storage key
    const token = localStorage.getItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY) || '';
    const baseWsUrl = config.apiUrl.replace(/^http/, 'ws') + '/ws';
    const wsUrl = token ? `${baseWsUrl}?token=${encodeURIComponent(token)}` : baseWsUrl;
    
    console.log('%c [WebSocketService] Connecting to WebSocket at: ' + baseWsUrl + (token ? ' (with auth token)' : ' (no auth token)'), 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;');
    console.log('%c [WebSocketService] Token found in localStorage:', !!token, 'background: #9b59b6; color: white; padding: 2px 5px; border-radius: 2px;');
    
    try {
      // Check if the browser supports WebSockets
      if (typeof WebSocket === 'undefined') {
        throw new Error('WebSockets are not supported in this browser');
      }
      
      // Log network conditions
      console.log('%c [WebSocketService] Network conditions:', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;', {
        online: navigator.onLine,
        userAgent: navigator.userAgent,
        clientId: localStorage.getItem('ws_client_id')
      });
      
      // Close any existing connection before creating a new one
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
        console.log('%c [WebSocketService] Closing existing connection before creating a new one', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;');
        this.intentionalClosure = true;
        this.ws.close();
      }
      
      // Create WebSocket with a timeout
      this.ws = new WebSocket(wsUrl);
      console.log('%c [WebSocketService] WebSocket instance created successfully', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;');
      
      // Set a connection timeout
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          console.error('%c [WebSocketService] WebSocket connection timed out', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;');
          this.ws.close();
        }
      }, 10000); // 10 second timeout
      
      // Clear the timeout if connection succeeds
      this.ws.addEventListener('open', () => {
        clearTimeout(connectionTimeout);
      });
    } catch (error) {
      console.error('%c [WebSocketService] Error creating WebSocket connection:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', error);
      // Return a subject that will immediately error out
      const errorSubject = new Subject<any>();
      setTimeout(() => errorSubject.error(error), 0);
      return errorSubject;
    }
    
    const observable = new Observable<MessageEvent>(observer => {
      if (null === this.ws) {
        observer.error(new Error('Failed to create WebSocket connection'));
        return;
      }
      
      this.ws.onopen = () => {
        console.log('%c [WebSocketService] WebSocket connection established', 'background: #2ecc71; color: white; padding: 2px 5px; border-radius: 2px;');
        this.isConnected = true;
        this.reconnectAttempts = 0;
      };
      
      this.ws.onmessage = (event) => {
        console.log('%c [WebSocketService] WebSocket message received:', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;', event);
        observer.next(event);
      };
      
      this.ws.onerror = (event) => {
        console.error('%c [WebSocketService] WebSocket error:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', event);
        console.error('%c [WebSocketService] WebSocket state at error:', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;', {
          readyState: this.ws ? this.getReadyStateString(this.ws.readyState) : 'Unknown',
          url: this.ws ? this.ws.url : 'Unknown'
        });
        this.isConnected = false;
        observer.error(event);
      };
      
      this.ws.onclose = (event) => {
        console.log('%c [WebSocketService] WebSocket connection closed:', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;', event, this.getCloseEventReason(event.code));
        this.isConnected = false;

        // Attempt to reconnect if not closed cleanly and we haven't exceeded max attempts
        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts && !this.intentionalClosure) {
          console.log(`Attempting to reconnect (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})...`);
          this.reconnectAttempts++;

          setTimeout(() => {
            console.log('%c [WebSocketService] Reconnecting via initializeConnection to re-establish message pipeline...', 'background: #9b59b6; color: white; padding: 2px 5px; border-radius: 2px;');
            // CRITICAL: Must call initializeConnection() instead of create() directly
            // This re-establishes the subscription pipeline to messagesSubject
            // Calling create() alone would create a new subject that's not wired to messagesSubject
            this.subject = null; // Clear old subject
            this.initializeConnection(true); // Re-establish full pipeline, pass true to preserve reconnect attempts
          }, this.reconnectInterval);
        } else {
          observer.complete();
        }
      };
      
      return () => {
        if (this.ws) {
          console.log('Closing WebSocket connection');
          this.ws.close();
          this.ws = null;
          this.isConnected = false;
        }
      };
    });
    
    const observer = {
      next: (data: any) => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.log('Sending message to WebSocket:', data);
          this.ws.send(JSON.stringify(data));
        } else {
          console.error('WebSocket not connected, cannot send message');
        }
      },
      error: (error: any) => {
        console.error('WebSocket observer error:', error);
        if (this.ws) {
          this.ws.close();
          this.ws = null;
          this.isConnected = false;
        }
      },
      complete: () => {
        console.log('WebSocket observer completed');
        if (this.ws) {
          this.ws.close();
          this.ws = null;
          this.isConnected = false;
        }
      }
    };
    
    return Subject.create(observer, observable);
  }

  public send(message: Message): void {
    console.log('Attempting to send message:', message);
    this.subject?.next(JSON.stringify(message) as any);
  }

  public closeConnection(): void {
    console.log('Closing WebSocket connection');
    
    // Clear the status check interval
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
    
    if (null !== this.ws && this.isConnected) {
      this.intentionalClosure = true;
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.subject = null;
    }
  }
  
  // Helper method to convert WebSocket readyState to a readable string
  private getReadyStateString(readyState: number): string {
    switch (readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING (0)';
      case WebSocket.OPEN:
        return 'OPEN (1)';
      case WebSocket.CLOSING:
        return 'CLOSING (2)';
      case WebSocket.CLOSED:
        return 'CLOSED (3)';
      default:
        return `UNKNOWN (${readyState})`;
    }
  }
  
  // Helper method to provide a description for WebSocket close event codes
  private getCloseEventReason(code: number): string {
    switch (code) {
      case 1000:
        return 'Normal closure';
      case 1001:
        return 'Going away';
      case 1002:
        return 'Protocol error';
      case 1003:
        return 'Unsupported data';
      case 1004:
        return 'Reserved';
      case 1005:
        return 'No status received';
      case 1006:
        return 'Abnormal closure';
      case 1007:
        return 'Invalid frame payload data';
      case 1008:
        return 'Policy violation';
      case 1009:
        return 'Message too big';
      case 1010:
        return 'Mandatory extension';
      case 1011:
        return 'Internal server error';
      case 1012:
        return 'Service restart';
      case 1013:
        return 'Try again later';
      case 1014:
        return 'Bad gateway';
      case 1015:
        return 'TLS handshake';
      default:
        return `Unknown code (${code})`;
    }
  }

  public close(): void {
    console.log('Closing WebSocket connection');
    
    // Clear the status check interval
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
    
    this.intentionalClosure = true;
    this.subject?.complete();
    this.subject = null;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}
